import React, { useState, useEffect } from 'react';
import { Input } from '../../../../../../components/Common/UI/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/Common/UI/Select';

export interface ViewerFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  securityQuestion: string;
  securityAnswer: string;
}

interface ViewerFormProps {
  initialData?: Partial<ViewerFormData>;
  onChange?: (data: ViewerFormData) => void;
  onValidationChange?: (isValid: boolean) => void;
  isEditing?: boolean;
}

export function ViewerForm({ initialData = {}, onChange, onValidationChange, isEditing = false }: ViewerFormProps) {
  const [formData, setFormData] = useState<ViewerFormData>(() => ({
    firstName: initialData.firstName ?? '',
    lastName: initialData.lastName ?? '',
    username: initialData.username ?? '',
    email: initialData.email ?? '',
    password: '',
    confirmPassword: '',
    securityQuestion: initialData.securityQuestion ?? '',
    securityAnswer: initialData.securityAnswer ?? '',
  }));

  const [errors, setErrors] = useState<Partial<Record<keyof ViewerFormData, string>>>({});

  const validatePasswords = (password: string, confirmPassword: string): string | undefined => {
    if (!isEditing && !password) {
      return 'Password is required';
    }
    if (password && password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (password && confirmPassword && password !== confirmPassword) {
      return 'Passwords do not match';
    }

    return undefined;
  };

  const validateForm = (data: ViewerFormData): boolean => {
    const passwordError = validatePasswords(data.password, data.confirmPassword);

    const newErrors: Partial<Record<keyof ViewerFormData, string>> = {};

    if (passwordError) {
      newErrors.password = passwordError;
      newErrors.confirmPassword = passwordError;
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;

    onValidationChange?.(isValid);

    return isValid;
  };

  useEffect(() => {
    setFormData({
      firstName: initialData.firstName ?? '',
      lastName: initialData.lastName ?? '',
      username: initialData.username ?? '',
      email: initialData.email ?? '',
      password: '',
      confirmPassword: '',
      securityQuestion: initialData.securityQuestion ?? '',
      securityAnswer: initialData.securityAnswer ?? '',
    });
  }, [
    initialData.firstName,
    initialData.lastName,
    initialData.username,
    initialData.email,
    initialData.securityQuestion,
    initialData.securityAnswer,
  ]);

  const handleChange = (field: keyof ViewerFormData, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (field === 'password' || field === 'confirmPassword') {
        validatePasswords(
          field === 'password' ? value : newData.password,
          field === 'confirmPassword' ? value : newData.confirmPassword,
        );
      }

      onChange?.(newData);

      return newData;
    });
  };

  const handleBlur = () => {
    validateForm(formData);
  };

  return (
    <div className="max-w-[700px] mx-auto space-y-5">
      {/* First Name */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          label="First Name"
          value={formData.firstName}
          onChange={e => handleChange('firstName', e.target.value)}
        />
      </div>

      {/* Last Name */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          label="Last Name (optional)"
          value={formData.lastName}
          onChange={e => handleChange('lastName', e.target.value)}
        />
      </div>

      {/* Username */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          label="Username"
          value={formData.username}
          onChange={e => handleChange('username', e.target.value)}
        />
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          label="Email"
          type="email"
          value={formData.email}
          onChange={e => handleChange('email', e.target.value)}
        />
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          type="password"
          label={isEditing ? 'New Password (leave empty to keep current)' : 'Password'}
          showPasswordToggle
          value={formData.password}
          onChange={e => handleChange('password', e.target.value)}
          onBlur={handleBlur}
          error={errors.password}
        />
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          type="password"
          label="Confirm Password"
          showPasswordToggle
          value={formData.confirmPassword}
          onChange={e => handleChange('confirmPassword', e.target.value)}
          onBlur={handleBlur}
          error={errors.confirmPassword}
        />
      </div>

      {/* Security Question */}
      <div>
        <Select value={formData.securityQuestion} onValueChange={value => handleChange('securityQuestion', value)}>
          <SelectTrigger variant="white" className="text-black h-12" label="Security Question" labelClassName="text-md">
            <SelectValue placeholder="Select a security question" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="zero">Which city were you born in?</SelectItem>
            <SelectItem value="one">{`What is your mother's maiden name?`}</SelectItem>
            <SelectItem value="two">{`What was your first pet's name?`}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Security Answer */}
      <div className="space-y-2">
        <Input
          variant="floating"
          sizeVariant="floating"
          label="Security Answer"
          value={formData.securityAnswer}
          onChange={e => handleChange('securityAnswer', e.target.value)}
        />
      </div>
    </div>
  );
}

export default ViewerForm;
